/**
 * Temperature limit lines for the dashboard charts, shared by Location
 * Details (desktop, full labels) and the phone charts (short labels), and
 * the one comparison rule for "outside a limit" on the client.
 *
 * Pure functions (no React, no API calls). Highcharts is imported for its
 * types only: the test stubs of Highcharts lack its helpers.
 */
import type Highcharts from 'highcharts/highstock';
import type { ChartLimit, Pour2Limit, TempLimit } from '../../services/pours';

// Limit "Sign" / LimitsType → comparison symbol for the plotline label.
// The pour wizard stores Upper/Lower; the defaults editors store the legacy
// 01–04 codes. Accept both so the label reflects how the limit was set.
export const LIMIT_OPERATOR_SYMBOLS: Record<string, string> = {
  '01': '>',
  '02': '>=',
  '03': '<',
  '04': '<=',
  Upper: '>=',
  Lower: '<=',
};

/** Label style of the lines: 'full' is the desktop label ("Max Temp >= 75°C",
 *  11px, 8px in from the right); 'short' the phone label ("Max ≥75°C", the
 *  name cut to 14 characters, 10px, 4px in). */
export type LimitLabelStyle = 'full' | 'short';

export interface TemperatureLimitLineOptions {
  /** Site-conditions hardware ('07' weather station, '09' Digit Sensor,
   *  '10' ConXenviro): the pour's temperature limits never apply, so there
   *  are no lines. */
  isSiteHardware: boolean;
  labels: LimitLabelStyle;
  /** 'short' labels only: the word for a limit without a name ('Limit'),
   *  so the phone can pass its translated word. */
  fallbackName?: string;
}

export interface TemperatureLimitLines {
  plotLines: Highcharts.YAxisPlotLinesOptions[];
  /** The limit values drawn. */
  values: number[];
  /** The highest limit plus 5 (room for its label), so every line stays on
   *  screen however cool the location runs; undefined without lines. */
  softMax: number | undefined;
}

/** Short names never take more than this many characters of a label. */
const SHORT_NAME_MAX = 14;

/** Unicode comparison symbols for the short labels ('>=' reads '≥'). */
const SHORT_OPERATOR_SYMBOLS: Record<string, string> = {
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
};

/** A limit's name cut to SHORT_NAME_MAX characters, with an ellipsis when
 *  cut. */
const shortName = (name: string): string => {
  const trimmed = name.trim();
  return trimmed.length > SHORT_NAME_MAX ? `${trimmed.slice(0, SHORT_NAME_MAX - 1).trimEnd()}…` : trimmed;
};

/** "Max ≥75°C": the short label of one limit. */
export const shortLimitLabel = (
  name: string | null | undefined,
  operator: string,
  value: number,
  unit = '°C',
  fallbackName = 'Limit',
): string => {
  const op = SHORT_OPERATOR_SYMBOLS[operator] ?? operator;
  return `${shortName(name || fallbackName)} ${op}${value}${unit}`;
};

/** The comparison symbol of a limit's operator: a symbol stays as it is, a
 *  stored code ('01'-'04', Upper, Lower) is mapped. */
const operatorSymbol = (operator: string | null | undefined): string =>
  LIMIT_OPERATOR_SYMBOLS[operator ?? ''] ?? (operator ?? '');

/**
 * Whether `value` is outside a limit: the same comparison as the Event Time
 * Line (eventTimelineModel isBreach) and the backend's current values
 * (chart_data_service._get_current_values). The operator is a symbol ('>',
 * '>=', '<', '<='); a stored code ('01'-'04', Upper, Lower) is mapped first.
 * Any other operator, or a value or limit that is not a number, is never a
 * breach.
 */
export const breaksLimit = (
  value: number | null | undefined,
  operator: string | null | undefined,
  limit: number | null | undefined,
): boolean => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (typeof limit !== 'number' || !Number.isFinite(limit)) return false;
  switch (operatorSymbol(operator)) {
    case '>': return value > limit;
    case '>=': return value >= limit;
    case '<': return value < limit;
    case '<=': return value <= limit;
    default: return false;
  }
};

/**
 * The temperature chart's limit plotlines. Prefer the pour's configured
 * limits (Criteria '02'), the same source the Temperature Profile bars read,
 * so the dashed lines always match the profile: real operator from
 * LimitsType, value from LimitsTemp, and the configured colour/Purpose.
 * Fall back to the per-location chart limits only when none were passed.
 * Site hardware gets none.
 */
export const temperatureLimitLines = (
  pourLimits: ReadonlyArray<Pour2Limit>,
  chartLimits: ReadonlyArray<ChartLimit> | null | undefined,
  { isSiteHardware, labels, fallbackName = 'Limit' }: TemperatureLimitLineOptions,
): TemperatureLimitLines => {
  const short = labels === 'short';
  const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
  const limitValues: number[] = [];
  const tempLimits = isSiteHardware
    ? []
    : pourLimits.filter((l) => l.Criteria === '02');
  if (tempLimits.length > 0) {
    tempLimits.forEach((limit) => {
      const op = LIMIT_OPERATOR_SYMBOLS[limit.LimitsType] || '>=';
      const colour = limit.BackgroundColor || 'red';
      const value = Number(limit.LimitsTemp);
      limitValues.push(value);
      plotLines.push({
        value,
        color: colour,
        width: 2,
        dashStyle: 'Dash',
        label: {
          text: short
            ? shortLimitLabel(limit.Purpose, op, value, '°C', fallbackName)
            : `${limit.Purpose || 'Limit'} ${op} ${value}°C`,
          align: 'right',
          x: short ? -4 : -8,
          style: { color: colour, fontSize: short ? '10px' : '11px', fontWeight: '600' }
        },
        zIndex: 5
      });
    });
  } else if (chartLimits && !isSiteHardware) {
    chartLimits.forEach((limit) => {
      limitValues.push(limit.value);
      plotLines.push({
        value: limit.value,
        color: 'red',
        width: 2,
        dashStyle: 'Dash',
        label: {
          text: short
            ? shortLimitLabel(limit.name, limit.operator, limit.value, '°C', fallbackName)
            : `Limit ${limit.operator} ${limit.value}°C`,
          align: 'right',
          x: short ? -4 : -8,
          style: short ? { color: 'red', fontSize: '10px' } : { color: 'red', fontSize: '11px' }
        },
        zIndex: 5
      });
    });
  }

  // Keep every limit line on-screen regardless of how cool the location
  // runs: softMax floors the axis maximum at the highest limit (+a little
  // headroom for its label) while still letting hotter data push higher.
  const softMax = limitValues.length > 0 ? Math.max(...limitValues) + 5 : undefined;
  return { plotLines, values: limitValues, softMax };
};

/** A limit as the phone draws it: a name, an operator symbol, a value and
 *  a colour (a location's own temperature limits from the dashboard, a
 *  differential or an evaporation limit). */
export interface PhoneLimit {
  name: string | null;
  operator: string;
  value: number;
  color: string | null;
}

/**
 * Short-labelled plotlines ("Max ≥75°C", 10px) for limits the phone already
 * has as values: a location's own temperature limits (TempLimit), its
 * differential limits or the evaporation limits. `unit` follows the value
 * ('°C', or '' for an evaporation rate); the softMax is the highest value
 * plus `headroom`.
 */
export const shortLimitLines = (
  limits: ReadonlyArray<PhoneLimit | TempLimit>,
  { unit = '°C', fallbackName = 'Limit', headroom = 5 }: { unit?: string; fallbackName?: string; headroom?: number } = {},
): TemperatureLimitLines => {
  const values: number[] = [];
  const plotLines: Highcharts.YAxisPlotLinesOptions[] = [];
  limits.forEach((limit) => {
    const value = Number(limit.value);
    if (!Number.isFinite(value)) return;
    const colour = limit.color || 'red';
    values.push(value);
    plotLines.push({
      value,
      color: colour,
      width: 2,
      dashStyle: 'Dash',
      label: {
        text: shortLimitLabel(limit.name, operatorSymbol(limit.operator) || '>=', value, unit, fallbackName),
        align: 'right',
        x: -4,
        style: { color: colour, fontSize: '10px', fontWeight: '600' },
      },
      zIndex: 5,
    });
  });
  return { plotLines, values, softMax: values.length > 0 ? Math.max(...values) + headroom : undefined };
};
